import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";
import "owl.carousel/dist/assets/owl.carousel.css";
import "./pms.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import moment from "moment";
import ERROR_LIST from "../../../Services/Global-Services/Hadle_Errors";
import ComponentLoader from "../../../Shared/Sidebar/ComponentLoader";

// IMPORTANT:
// Import your existing API service here

import { TostMsg } from "../../../App";
import {
  GetAssessmentCycleById,
  SendAppraisalNotification,
  SendCycleReminder,
} from "./PMSService";
// ^ Change this import path according to your project structure

/* ============================================================
   INTERNAL CSS
   (All classes are scoped under .assessment-module and use the
   "acd-" prefix so they don't clash with existing og-* classes)
============================================================ */

export const AssessmentCycleDetails = () => {
  const userDetails = JSON.parse(localStorage.getItem("User") || "{}");

  const toster = useContext(TostMsg);
  const history = useHistory();

  const { guid } = useParams();

  const [loading, setLoading] = useState(false);

  const [cycleDetails, setCycleDetails] = useState(null);
  const [employees, setEmployees] = useState([]);

  const [showReminderModal, setShowReminderModal] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  // ============================================================
  // REMINDER MODAL STATES
  // ============================================================

  const [showGroupReminderModal, setShowGroupReminderModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showReminderSuccess, setShowReminderSuccess] = useState(false);

  // Selected employee for individual reminder
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Selected notification type
  const [selectedNotificationType, setSelectedNotificationType] = useState("");

  // Custom message for individual reminder
  const [notificationMessage, setNotificationMessage] = useState("");

  // Group reminder selections
  const [groupReminderOptions, setGroupReminderOptions] = useState({
    SendInitiation: false,
    SendSelfReminder: false,
    SendManagerReminder: false,
    SendSkipReminder: false,
    SendAckReminder: false,
  });

  // Group reminder custom message
  const [groupReminderMessage, setGroupReminderMessage] = useState("");

  // API loading states
  const [sendingNotification, setSendingNotification] = useState(false);
  const [sendingGroupReminder, setSendingGroupReminder] = useState(false);

  // Success information
  const [reminderSuccessMessage, setReminderSuccessMessage] = useState("");

  const handleTabChange = (tab) => {
    console.log(tab);
    setActiveTab(tab);
  };

  // ============================================================
  // GET CYCLE DETAILS
  // ============================================================

  const getCycleDetails = useCallback(async () => {
    if (!guid) return;

    setLoading(true);

    const body = {
      Guid: guid,
    };

    try {
      const response = await GetAssessmentCycleById(body);

      console.log("Assessment Cycle Details API:", response);

      const data = response?.Data || response?.data || {};

      const cycle =
        data?.assessmentCycle || data?.assessmentCycleDetails || data;

      const employeeList = data?.Employees || [];

      setCycleDetails(cycle);
      setEmployees(employeeList);
      setLoading(false);
    } catch (error) {
      console.error("Get Assessment Cycle Details Error:", error);

      toster?.current?.error(
        ERROR_LIST?.EmpAssessmentList ||
          "Unable to fetch assessment cycle details",
      );
    } finally {
      setLoading(false);
    }
  }, [guid]);

  useEffect(() => {
    getCycleDetails();
  }, [getCycleDetails]);


  

  const handleExport = () => {
    const exportEmployees = filteredEmployees || [];

    if (exportEmployees.length === 0) {
      toster?.current?.error("No employees available to export");
      return;
    }

    const headers = [
      "Employee ID",
      "Employee Name",
      "Email",
      "Department",
      "Designation",
    ];

    const rows = exportEmployees.map((employee) => [
      employee?.EmpId ?? employee?.EmployeeId ?? employee?.Id ?? "",
      employee?.EmployeeName ?? employee?.FullName ?? employee?.Name ?? "",
      employee?.Email ?? employee?.EmailId ?? "",
      employee?.Department ?? "",
      employee?.Designation ?? "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => {
            const text = String(value ?? "");

            // Escape commas, quotes and new lines
            return `"${text.replace(/"/g, '""')}"`;
          })
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    const cycleTitle =
      cycleDetails?.Title || cycleDetails?.Name || "Assessment_Cycle";

    const safeCycleTitle = cycleTitle
      .replace(/[^a-z0-9]/gi, "_")
      .replace(/_+/g, "_");

    link.download = `${safeCycleTitle}_Employees.csv`;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    toster?.current?.success(
      `${exportEmployees.length} employee(s) exported successfully`,
    );
  };

  // ============================================================
  // DATE FORMATTER
  // ============================================================

  const formatDate = (date) => {
    if (!date) return "—";

    const parsedDate = moment(date);

    if (!parsedDate.isValid()) {
      return "—";
    }

    return parsedDate.format("MMM DD YYYY");
  };

  // ============================================================
  // GET DIFFERENT POSSIBLE API PROPERTY NAMES
  // ============================================================

  const getValue = (obj, keys, defaultValue = "") => {
    for (const key of keys) {
      if (obj?.[key] !== undefined && obj?.[key] !== null) {
        return obj[key];
      }
    }

    return defaultValue;
  };

  // ============================================================
  // CYCLE INFORMATION
  // ============================================================

  const cycleTitle = getValue(
    cycleDetails,
    [
      "Title",
      "title",
      "CycleTitle",
      "cycleTitle",
      "AssessmentTitle",
      "assessmentTitle",
    ],
    "Assessment Cycle",
  );

  const reviewPeriodFrom = getValue(cycleDetails, [
    "ReviewPeriodFrom",
    "reviewPeriodFrom",
    "ReviewFrom",
  ]);

  const reviewPeriodTo = getValue(cycleDetails, [
    "ReviewPeriodTo",
    "reviewPeriodTo",
    "ReviewTo",
  ]);

  const cycleStartDate = getValue(cycleDetails, [
    "CycleStartDate",
    "cycleStartDate",
    "StartDate",
  ]);

  const cycleEndDate = getValue(cycleDetails, [
    "CycleEndDate",
    "cycleEndDate",
    "EndDate",
  ]);

  const cycleStatus = getValue(
    cycleDetails,
    ["Status", "status", "CycleStatus", "cycleStatus"],
    "Initiated",
  );

  // ============================================================
  // TIMELINE DATES
  // ============================================================

  const selfAssessmentDeadline = getValue(cycleDetails, [
    "SelfAssessmentDeadline",
    "selfAssessmentDeadline",
    "SelfAssessmentBy",
  ]);

  const managerReviewDeadline = getValue(cycleDetails, [
    "ManagerReviewDeadline",
    "managerReviewDeadline",
    "ManagerReviewBy",
  ]);

  const skipLevelReviewDeadline = getValue(cycleDetails, [
    "SkipLevelReviewDeadline",
    "skipLevelReviewDeadline",
    "SkipLevelReviewBy",
  ]);

  const normalizationDeadline = getValue(cycleDetails, [
    "NormalizationDeadline",
    "normalizationDeadline",
    "NormalizationBy",
  ]);

  // ============================================================
  // EMPLOYEE COUNTS
  // ============================================================

  const totalEmployees =
    getValue(
      cycleDetails,
      ["TotalEmployees", "totalEmployees", "EmployeeCount"],
      employees?.length,
    ) || 0;

  const selfSubmitted =
    getValue(
      cycleDetails,
      ["SelfSubmitted", "selfSubmitted", "SelfSubmittedCount"],
      0,
    ) || 0;

  const managerReviewed =
    getValue(
      cycleDetails,
      ["ManagerReviewed", "managerReviewed", "ManagerReviewedCount"],
      0,
    ) || 0;

  const completed =
    getValue(cycleDetails, ["Completed", "completed", "CompletedCount"], 0) ||
    0;

  // ============================================================
  // PERCENTAGE
  // ============================================================

  const getPercentage = (count) => {
    if (!totalEmployees) return 0;

    return Math.round((Number(count) / Number(totalEmployees)) * 100);
  };

  // ============================================================
  // EMPLOYEE DATA MAPPING
  // ============================================================

  const getEmployeeName = (employee) => {
    return getValue(
      employee,
      ["FullName", "fullName", "EmployeeName", "employeeName", "Name", "name"],
      "—",
    );
  };

  const getManagerName = (employee) => {
    return getValue(
      employee,
      [
        "ManagerName",
        "managerName",
        "ReportingManagerName",
        "ReportingManager",
      ],
      "—",
    );
  };

   const getSkipLevelName = (employee) => {
    return getValue(
      employee,
      [
        "SkipLevelName"
      ],
      "—",
    );
  };

   const getNormalizationName = (employee) => {
    return getValue(
      employee,
      [
        "NormalizationActorName"
      ],
      "—",
    );
  };

  const getDepartment = (employee) => {
    return getValue(
      employee,
      ["DepartmentName", "departmentName", "Department"],
      "—",
    );
  };

  const getDesignation = (employee) => {
    return getValue(
      employee,
      ["DesignationName", "designationName", "Designation"],
      "",
    );
  };

  const getStage = (employee) => {
    return getValue(employee, ["StageName"], "—");
  };

  const getScore = (employee) => {
    return getValue(employee, ["ComputedScore"], null);
  };

  const getRating = (employee) => {
    return getValue(employee, ["RatingLabelName"], "—");
  };

  const getPromotion = (employee) => {
    return getValue(employee, ["IsPromotionRecommended"], "-");
  };

  const getPIP = (employee) => {
    return getValue(employee, ["IsPIPRecommended"], "-");
  };

  const filteredEmployees = useMemo(() => {
    let result = employees;

    // Apply tab filter
    if (activeTab !== "all") {
      result = result.filter((employee) => {
        const stage = String(getStage(employee)).toLowerCase();

        switch (activeTab) {
          case "selfPending":
            return stage.includes("self assessment");

          case "managerReview":
            return stage.includes("manager review");

          case "normalization":
            return stage.includes("normalization");

          case "completed":
            return stage.includes("complete");

          default:
            return true;
        }
      });
    }

    // Apply dynamic search
    const searchValue = employeeSearch.trim().toLowerCase();

    if (searchValue) {
      result = result.filter((employee) => {
        const employeeName = getEmployeeName(employee);
        const managerName = getManagerName(employee);
        const department = getDepartment(employee);
        const designation = getDesignation(employee);

        return [
          employeeName,
          managerName,
          department,
          designation,
          employee?.Email,
          employee?.UserName,
          employee?.EmployeeCode,
        ].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(searchValue),
        );
      });
    }

    return result;
  }, [employees, activeTab, employeeSearch]);

  // ============================================================
  // STAGE CLASS
  // ============================================================

  const getStageClass = (stage) => {
    const value = String(stage || "").toLowerCase();

    if (value.includes("self")) {
      return "badge-self";
    }

    if (value.includes("manager")) {
      return "badge-review";
    }

    if (value.includes("normal")) {
      return "badge-normalization";
    }

    if (value.includes("complete")) {
      return "badge-completed";
    }

    return "badge-review";
  };

  // ============================================================
  // RATING CLASS
  // ============================================================

  const getRatingClass = (rating) => {
    const value = String(rating || "").toLowerCase();

    if (value.includes("stellar")) {
      return "badge-stellar";
    }

    if (value.includes("adequate") || value.includes("average")) {
      return "badge-adequate";
    }

    return "badge-adequate";
  };

  // ============================================================
  // VIEW EMPLOYEE
  // ============================================================

  const handleViewEmployee = (employee) => {
    const employeeGuid = employee?.AppraisalGuid; //getValue(employee, ["AppraisalGuid"]);

    if (!employeeGuid) {
      console.warn("Employee GUID not found", employee);

      return;
    }

    history.push(`/Assessment/Review/${employeeGuid}`);
  };

  // ============================================================
  // REMINDER MODAL
  // ============================================================

  // ============================================================
  // OPEN INDIVIDUAL REMINDER MODAL
  // ============================================================

  const openReminderModal = (employee) => {
    if (!employee) {
      toster?.current?.error("Employee information not found");
      return;
    }

    if (!employee?.AppraisalId) {
      console.error("AppraisalId missing:", employee);

      toster?.current?.error("Appraisal ID is missing for this employee");

      return;
    }

    setSelectedEmployee(employee);
    setSelectedNotificationType("");
    setNotificationMessage("");
    setShowReminderModal(true);
  };

  // ============================================================
  // CLOSE INDIVIDUAL REMINDER OPTIONS
  // ============================================================

  const closeReminderModal = () => {
    setShowReminderModal(false);
  };

  // ============================================================
  // OPEN SPECIFIC NOTIFICATION
  // ============================================================

  const openNotificationModal = (notificationType) => {
    if (!selectedEmployee?.AppraisalId) {
      toster?.current?.error("Appraisal ID not found");
      return;
    }

    setSelectedNotificationType(notificationType);
    setNotificationMessage("");
    setShowReminderModal(false);
    setShowNotificationModal(true);
  };

  // ============================================================
  // CLOSE NOTIFICATION MODAL
  // ============================================================

  const closeNotificationModal = () => {
    if (sendingNotification) return;

    setShowNotificationModal(false);
    setSelectedNotificationType("");
    setNotificationMessage("");
  };

  // ============================================================
  // SEND INDIVIDUAL NOTIFICATION
  // ============================================================

  const handleSendNotification = async () => {
    if (!selectedEmployee?.AppraisalId) {
      toster?.current?.error("Appraisal ID is missing");
      return;
    }

    if (!selectedNotificationType) {
      toster?.current?.error("Please select a notification type");
      return;
    }

    try {
      setSendingNotification(true);
      setLoading(true);

      const params = {
        NotificationType: selectedNotificationType,
        CustomMessage: notificationMessage || "",
      };

      const response = await SendAppraisalNotification(
        selectedEmployee.AppraisalId,
        params,
      );

      console.log("Notification API Response:", response);

      const employeeName = getEmployeeName(selectedEmployee);

      const reminderLabel =
        reminderOptions.find((item) => item.type === selectedNotificationType)
          ?.title || "Reminder";

      setShowNotificationModal(false);

      setReminderSuccessMessage(
        `${reminderLabel} has been sent to ${employeeName}.`,
      );

      setShowReminderSuccess(true);

      toster?.current?.success(`${reminderLabel} sent successfully`);
      setLoading(false);
    } catch (error) {
      console.error("Send notification error:", error);

      toster?.current?.error(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "Unable to send reminder",
      );
    } finally {
      setSendingNotification(false);
      setLoading(false);
    }
  };

  // ============================================================
  // GROUP REMINDER
  // ============================================================

  const openGroupReminderModal = () => {
    setGroupReminderOptions({
      SendInitiation: false,
      SendSelfReminder: false,
      SendManagerReminder: false,
      SendSkipReminder: false,
      SendAckReminder: false,
    });

    setGroupReminderMessage("");

    setShowGroupReminderModal(true);
  };

  // ============================================================
  // CLOSE GROUP REMINDER
  // ============================================================

  const closeGroupReminderModal = () => {
    if (sendingGroupReminder) return;

    setShowGroupReminderModal(false);
  };

  // ============================================================
  // TOGGLE GROUP REMINDER OPTION
  // ============================================================

  const toggleGroupReminderOption = (key) => {
    setGroupReminderOptions((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  // ============================================================
  // SEND GROUP REMINDER
  // ============================================================

  const handleSendGroupReminder = async () => {
    const cycleId = cycleDetails?.Id; //getCycleId();

    if (!cycleId) {
      console.error("Invalid cycle ID:", cycleDetails);

      toster?.current?.error("Cycle ID is missing or invalid");

      return;
    }

    const hasSelection = Object.values(groupReminderOptions).some(Boolean);

    if (!hasSelection) {
      toster?.current?.error("Please select at least one reminder type");

      return;
    }

    const count = Object.values(groupReminderOptions).filter(value => value === true).length

    try {
      // setSendingGroupReminder(true);
setLoading(true);
      const params = {
        SendInitiation: groupReminderOptions.SendInitiation,

        SendSelfReminder: groupReminderOptions.SendSelfReminder,

        SendManagerReminder: groupReminderOptions.SendManagerReminder,

        SendSkipReminder: groupReminderOptions.SendSkipReminder,

        SendAckReminder: groupReminderOptions.SendAckReminder,

        CustomMessage: groupReminderMessage || "",
      };

      const response = await SendCycleReminder(cycleId, params);

      console.log("Group Reminder API Response:", response);

      const data = response?.Data || response?.data || response || {};

      const totalSent = Number(data?.TotalSent ?? data?.totalSent ?? 0);

      setShowGroupReminderModal(false);

      // setReminderSuccessMessage(
      //   `${totalSent} reminder${totalSent === 1 ? "" : "s"} sent successfully.`,
      // );
if(response?.StatusCode === 2000){
      setReminderSuccessMessage(`Group reminders have been successfully sent to ${totalSent} recipient${totalSent > 1  ? "s" : ""} across ${count} selected group${count > 1 ? 's' : ''} for ${cycleDetails?.Title}.`)

      setShowReminderSuccess(true);

      toster?.current?.success(
        `${totalSent} reminder${totalSent === 1 ? "" : "s"} sent successfully`,
      );
    }

      setLoading(false);
    } catch (error) {
      console.error("Group reminder error:", error);

      toster?.current?.error(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "Unable to send group reminders",
      );
    } finally {
      setSendingGroupReminder(false);
      setLoading(false);
    }
  };

  // ============================================================
  // GET REMINDER RECIPIENT
  // ============================================================

  const getNotificationRecipient = (notificationType, employee) => {
    if (!employee) return "Employee";

    switch (notificationType) {
      case "Initiation":
        return getEmployeeName(employee);

      case "SelfReminder":
        return getEmployeeName(employee);

      case "ManagerReminder":
        return getManagerName(employee);

      case "SkipReminder":
        return getValue(
          employee,
          [
            "SkipLevelReviewerName",
            "SecondReviewerName",
            "SkipReviewerName",
            "SkipLevelName",
          ],
          "Skip-Level Reviewer",
        );

      case "AckReminder":
        return getEmployeeName(employee);

      default:
        return getEmployeeName(employee);
    }
  };

  // ============================================================
  // INDIVIDUAL REMINDER CONFIGURATION
  // ============================================================

  const reminderOptions = [
    {
      type: "Initiation",
      icon: "📨",
      title: "Send Initiation Email",
      description: "Resend the cycle kickoff email to this employee",
    },
    {
      type: "SelfReminder",
      icon: "⏰",
      title: "Self Assessment Reminder",
      description: "Remind employee to complete their self-assessment",
    },
    {
      type: "ManagerReminder",
      icon: "👤",
      title: "Manager Review Reminder",
      description: "Send reminder to the first reviewer / manager",
    },
    {
      type: "SkipReminder",
      icon: "⏭️",
      title: "Skip-Level Reminder",
      description: "Send reminder to the skip-level reviewer",
    },
    {
      type: "AckReminder",
      icon: "✅",
      title: "Acknowledgement Reminder",
      description: "Remind employee to acknowledge their final result",
    },
  ];

  // ============================================================
  // GROUP REMINDER CONFIGURATION
  // ============================================================

  const groupReminderOptionsConfig = [
    {
      key: "SendInitiation",
      title: "Initiation Email",
      description:
        "Send the cycle kickoff email to employees who haven't received it yet.",
      icon: "📨",
      countKeys: [
        "PendingInitiation",
        "pendingInitiation",
        "PendingInitiationEmails",
        "pendingInitiationEmails",
      ],
    },
    {
      key: "SendSelfReminder",
      title: "Pending Self-Assessment",
      description:
        "Remind employees who have not yet submitted their self-assessment.",
      icon: "⏰",
      countKeys: [
        "PendingSelfReviews",
        "pendingSelfReviews",
        "PendingSelfAssessment",
        "pendingSelfAssessment",
      ],
    },
    {
      key: "SendManagerReminder",
      title: "Pending Manager Reviews",
      description:
        "Remind managers whose team members are awaiting manager review.",
      icon: "👤",
      countKeys: ["PendingManagerReviews", "pendingManagerReviews"],
    },
    {
      key: "SendSkipReminder",
      title: "Pending Skip-Level Reviews",
      description: "Remind skip-level reviewers who have pending reviews.",
      icon: "⏭️",
      countKeys: ["PendingSkipLevelReviews", "pendingSkipLevelReviews"],
    },
    {
      key: "SendAckReminder",
      title: "Pending Acknowledgement",
      description: "Remind employees to acknowledge their appraisal result.",
      icon: "✅",
      countKeys: [
        "PendingNormalization",
        "pendingNormalization",
        "PendingAcknowledgement",
        "pendingAcknowledgement",
      ],
    },
  ];

 

  // ============================================================
  // CALCULATE COUNTS FROM EMPLOYEES
  // ============================================================

  const calculatedCounts = {
    selfPending: employees.filter((item) =>
      String(getStage(item)).toLowerCase().includes("self"),
    ).length,

    managerReview: employees.filter((item) =>
      String(getStage(item)).toLowerCase().includes("manager"),
    ).length,

    normalization: employees.filter((item) =>
      String(getStage(item)).toLowerCase().includes("normal"),
    ).length,

    completed: employees.filter((item) =>
      String(getStage(item)).toLowerCase().includes("complete"),
    ).length,
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <div>
      {/* Internal CSS */}

      {/* <div className="items-wrapper">

        <div className="menu-item is-flex">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="feather feather-bell"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
        </div>

        <a
          className="menu-item is-flex right-panel-trigger"
          data-panel="languages-panel"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="feather feather-bell"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
        </a>

        <Link
          to=""
          className="menu-item is-flex"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="feather feather-bell"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
        </Link>

        <a
          className="menu-item is-flex right-panel-trigger"
          data-panel="activity-panel"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="feather feather-grid"
          >
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect
              x="14"
              y="14"
              width="7"
              height="7"
            ></rect>
            <rect
              x="3"
              y="14"
              width="7"
              height="7"
            ></rect>
          </svg>
        </a>

      </div> */}

      <div className="container-fluid">
        <div className="row g-3 assessment-module">
          <div className="og-main">
            {/* =====================================================
                HEADER
            ====================================================== */}

            <div className="og-page-header Page-head">
              <div className="leftside-head">
                <div className="og-breadcrumb">
                  <Link to="/Assessment/Cycle" className="acd-breadcrumb-link">
                    Assessment Cycles
                  </Link>

                  {" › "}

                  {cycleTitle}
                </div>

                <div className="page-title">{cycleTitle}</div>

                <div className="og-page-sub breadcrumb">
                  Review Period: {formatDate(reviewPeriodFrom)}
                  {" – "}
                  {formatDate(reviewPeriodTo)}
                  {"  ·  "}
                  Cycle: {formatDate(cycleStartDate)}
                  {" – "}
                  {formatDate(cycleEndDate)}
                </div>
              </div>

              <div className="acd-header-actions">
                <button
                  type="button"
                  className="btn btn-outline btn-sm btn-gray"
                  onClick={openGroupReminderModal}
                >
                  <i className="fa fa-bell"></i> Group Reminder
                </button>

                <span className="btn btn-sm btn-primary">{cycleStatus}</span>
              </div>
            </div>

            {/* =====================================================
                TIMELINE
            ====================================================== */}

            <div className="summary">
              <div className="og-card">
                <div className="og-timeline border-0">
                  <div className="og-tl-item">
                    <div className="og-tl-label">
                      <i className="fa fa-play-circle acd-c-orange"></i> Cycle
                      Started
                    </div>

                    <div className="og-tl-date">
                      {formatDate(cycleStartDate)}
                    </div>
                  </div>

                  <div className="og-tl-item">
                    <div className="og-tl-label">
                      <i className="fa fa-pen-to-square acd-c-amber"></i> Self
                      Assessment By
                    </div>

                    <div className="og-tl-date acd-c-amber">
                      {formatDate(selfAssessmentDeadline)}
                    </div>
                  </div>

                  <div className="og-tl-item">
                    <div className="og-tl-label">
                      <i className="fa fa-user-check acd-c-purple"></i> Manager
                      Review By
                    </div>

                    <div className="og-tl-date">
                      {formatDate(managerReviewDeadline)}
                    </div>
                  </div>

                  <div className="og-tl-item">
                    <div className="og-tl-label">
                      <i className="fa fa-flag-checkered acd-c-green"></i>{" "}
                      Normalization By
                    </div>

                    <div className="og-tl-date">
                      {formatDate(normalizationDeadline)}
                    </div>
                  </div>
                </div>
              </div>

              {/* =================================================
                  SUMMARY COUNTS
              ================================================== */}

              <div className="og-card">
                <div className="acd-sum-row">
                  <span className="acd-c-muted">Total Employees</span>

                  <strong>{totalEmployees}</strong>
                </div>

                <div className="acd-sum-row">
                  <span className="acd-c-muted">Self Submitted</span>

                  <strong className="acd-c-green">
                    {selfSubmitted} ({getPercentage(selfSubmitted)}
                    %)
                  </strong>
                </div>

                <div className="acd-sum-row">
                  <span className="acd-c-muted">Manager Reviewed</span>

                  <strong className="acd-c-purple">
                    {managerReviewed} ({getPercentage(managerReviewed)}
                    %)
                  </strong>
                </div>

                <div className="acd-sum-row">
                  <span className="acd-c-muted">Completed</span>

                  <strong>
                    {completed} ({getPercentage(completed)}
                    %)
                  </strong>
                </div>
              </div>
            </div>

            {/* =====================================================
                EMPLOYEE TABLE
            ====================================================== */}

            <div className="og-card">
              <div className="acd-toolbar">
                <div className="acd-tabs">
                  <button
                    type="button"
                    className={`btn-og ${
                      activeTab === "all"
                        ? "btn-og-primary"
                        : "btn-og-secondary"
                    } btn-og-sm`}
                    onClick={() => handleTabChange("all")}
                  >
                    All ({totalEmployees})
                  </button>

                  <button
                    type="button"
                    className={`btn-og ${
                      activeTab === "selfPending"
                        ? "btn-og-primary"
                        : "btn-og-secondary"
                    } btn-og-sm`}
                    onClick={() => handleTabChange("selfPending")}
                  >
                    Self Pending ({calculatedCounts.selfPending})
                  </button>

                  <button
                    type="button"
                    className={`btn-og ${
                      activeTab === "managerReview"
                        ? "btn-og-primary"
                        : "btn-og-secondary"
                    } btn-og-sm`}
                    onClick={() => handleTabChange("managerReview")}
                  >
                    Manager Review ({calculatedCounts.managerReview})
                  </button>

                  <button
                    type="button"
                    className={`btn-og ${
                      activeTab === "normalization"
                        ? "btn-og-primary"
                        : "btn-og-secondary"
                    } btn-og-sm`}
                    onClick={() => handleTabChange("normalization")}
                  >
                    Normalization ({calculatedCounts.normalization})
                  </button>

                  <button
                    type="button"
                    className={`btn-og ${
                      activeTab === "completed"
                        ? "btn-og-primary"
                        : "btn-og-secondary"
                    } btn-og-sm`}
                    onClick={() => handleTabChange("completed")}
                  >
                    Completed ({calculatedCounts.completed})
                  </button>
                </div>

                <div className="acd-toolbar-right">
                  <input
                    className="og-input btn-sm acd-search"
                    type="text"
                    placeholder="Search..."
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                  />

                  <button
                    type="button"
                    className="btn-og btn-og-secondary btn-sm"
                    onClick={handleExport}
                    disabled={filteredEmployees.length === 0}
                  >
                    <i className="fa fa-download"></i> Export
                  </button>
                </div>
              </div>

              <div className="og-table-wrap table-responsive">
                <table className="og-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Department</th>
                      <th>Manager</th>
                      <th>Skip-level</th>
                      <th>Normalization</th>
                      <th>Stage</th>
                      <th>Computed Score</th>
                      <th>Rating Label</th>
                      <th>Promotion</th>
                      <th>PIP</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredEmployees?.length > 0 ? (
                      filteredEmployees.map((employee, index) => {
                        const employeeName = getEmployeeName(employee);

                        const managerName = getManagerName(employee);

                        const skipLevelName = getSkipLevelName(employee);

                        const normalizationName = getNormalizationName(employee);

                        const department = getDepartment(employee);

                        const designation = getDesignation(employee);

                        const stage = getStage(employee);

                        const score = getScore(employee);

                        const rating = getRating(employee);

                        const promotion = getPromotion(employee);

                        const pip = getPIP(employee);

                        const initials = employeeName
                          ?.split(" ")
                          ?.map((name) => name?.[0])
                          ?.join("")
                          ?.substring(0, 2)
                          ?.toUpperCase();

                        return (
                          <tr
                            key={getValue(
                              employee,
                              [
                                "GUID",
                                "Guid",
                                "guid",
                                "EmployeeGuid",
                                "Id",
                                "ID",
                              ],
                              index,
                            )}
                          >
                            {/* EMPLOYEE */}

                            <td>
                              <div className="og-emp-cell">
                                <div className="og-emp-av acd-av-orange">
                                  {initials}
                                </div>

                                <div>
                                  <div className="acd-emp-name">
                                    {employeeName}
                                  </div>

                                  <div className="acd-emp-desig">
                                    {designation}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* DEPARTMENT */}

                            <td>{department}</td>

                            {/* MANAGER */}

                            <td>
                              <div className="og-emp-cell text-nowrap">
                                <div className="og-emp-av acd-av-mgr">
                                  {managerName
                                    ?.split(" ")
                                    ?.map((item) => item?.[0])
                                    ?.join("")
                                    ?.substring(0, 2)
                                    ?.toUpperCase()}
                                </div>

                                <span className="acd-mgr-name">
                                  {managerName}
                                </span>
                              </div>
                            </td>

<td>
                              <div className="og-emp-cell text-nowrap">
                                <div className="og-emp-av acd-av-mgr">
                                  {skipLevelName
                                    ?.split(" ")
                                    ?.map((item) => item?.[0])
                                    ?.join("")
                                    ?.substring(0, 2)
                                    ?.toUpperCase()}
                                </div>

                                <span className="acd-mgr-name">
                                  {skipLevelName}
                                </span>
                              </div>
                            </td>

                            <td>
                              <div className="og-emp-cell text-nowrap">
                                <div className="og-emp-av acd-av-mgr">
                                  {normalizationName
                                    ?.split(" ")
                                    ?.map((item) => item?.[0])
                                    ?.join("")
                                    ?.substring(0, 2)
                                    ?.toUpperCase()}
                                </div>

                                <span className="acd-mgr-name">
                                  {normalizationName}
                                </span>
                              </div>
                            </td>

                            


                            {/* STAGE */}

                            <td>
                              <span
                                className={`badge-og ${getStageClass(stage)}`}
                              >
                                {stage}
                              </span>
                            </td>

                            {/* SCORE */}

                            <td>
                              {score !== null &&
                              score !== undefined &&
                              score !== "" ? (
                                <>
                                  <strong className="acd-score">{score}</strong>

                                  <span className="acd-score-max">/5.0</span>
                                </>
                              ) : (
                                "—"
                              )}
                            </td>

                            {/* RATING */}

                            <td>
                              {rating !== "—" ? (
                                <span
                                  className={`badge-og ${getRatingClass(
                                    rating,
                                  )}`}
                                >
                                  {rating}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>

                            {/* PROMOTION */}

                            <td>
                              {/* {promotion} */}

                              {promotion === true ||
                              promotion === "true" ||
                              promotion === 1 ? (
                                <i className="fa fa-check-circle acd-c-orange"></i>
                              ) : (
                                "—"
                              )}
                            </td>

                            <td>
                              {/* {pip} */}

                              {pip === true || pip === "true" || pip === 1 ? (
                                <i className="fa fa-check-circle acd-c-orange"></i>
                              ) : (
                                "—"
                              )}
                            </td>

                            {/* ACTIONS */}

                            <td>
                              <div className="btn-row">
                                {String(stage)
                                  .toLowerCase()
                                  .includes("complete") && (
                                  <button
                                    type="button"
                                    className="btn-og btn-og-secondary btn-og-sm"
                                  >
                                    <i className="fa fa-file-lines"></i> Letter
                                  </button>
                                )}

                                <button
                                  type="button"
                                  className="btn-og btn-og-secondary btn-og-sm"
                                  onClick={() => handleViewEmployee(employee)}
                                >
                                  <i className="fa fa-eye"></i> View
                                </button>

{employee?.StageId <= 1 && (
                                <button
                                  type="button"
                                  className="btn btn-primary btn-og-sm"
                                  onClick={() => openReminderModal(employee)}
                                >
                                  <i className="fa fa-bell"></i> Remind
                                </button>
                                )}  
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="acd-empty">
                          No employees found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* =====================================================
                REMINDER MODAL
            ====================================================== */}

            {/* ============================================================
    INDIVIDUAL REMINDER OPTIONS
============================================================ */}

            {showReminderModal && selectedEmployee && (
              <div className="og-modal-overlay open">
                <div className="og-modal">
                  <div className="og-modal-header">
                    <div className="og-modal-title">
                      <i className="fa fa-bell acd-c-orange"></i> Send Reminder
                    </div>

                    <button
                      type="button"
                      className="og-modal-close"
                      onClick={closeReminderModal}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </div>

                  <div className="modal-body p-2">
                    {/* EMPLOYEE */}
                    <div className="emp-target">
                      <div className="avatar notification-avatar">
                        {getEmployeeName(selectedEmployee)
                          ?.split(" ")
                          ?.map((item) => item?.[0])
                          ?.join("")
                          ?.substring(0, 2)
                          ?.toUpperCase()}
                      </div>

                      <div className="target-info">
                        <div className="target-name">
                          {getEmployeeName(selectedEmployee)}
                        </div>

                        <div className="target-meta">
                          {getValue(
                            selectedEmployee,
                            ["EmployeeCode", "EmpId", "EmployeeId"],
                            "—",
                          )}{" "}
                          · {getDesignation(selectedEmployee)}
                        </div>

                        <span className="target-stage">
                          {getStage(selectedEmployee)}
                        </span>
                      </div>
                    </div>

                    {/* REMINDER OPTIONS */}
                    <div className="opt-list">
                      {reminderOptions.map((option) => (
                        <div
                          key={option.type}
                          type="button"
                          className="opt-item"
                          onClick={() => openNotificationModal(option.type)}
                        >
                          <div
                            className={`opt-icon notification-icon-${option.type}`}
                          >
                            {option.icon}
                          </div>

                          <div className="opt-text">
                            <div className="opt-title">{option.title}</div>

                            <div className="opt-sub">{option.description}</div>
                          </div>

                          <span className="opt-arrow">›</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="og-modal-footer">
                    <button
                      type="button"
                      className="btn-og btn-og-secondary"
                      onClick={closeReminderModal}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================
    GROUP REMINDER POPUP
============================================================ */}

            {showGroupReminderModal && (
              <div className={`og-modal-overlay ${showGroupReminderModal ? 'open' : ''}`}>
                <div className="og-modal">
                  <div className="og-modal-header">
                    <div className="og-modal-title">
                      <i className="fa fa-bell acd-c-orange"></i> Send Group
                      Reminder
                    </div>

                    <button
                      type="button"
                      className="og-modal-close"
                      onClick={closeGroupReminderModal}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </div>

                  <div className="modal-body">
                    <div className="section-lbl">Notification Groups</div>

                    {groupReminderOptionsConfig.map((option) => {
                      const count =
                        Number(getValue(cycleDetails, option.countKeys, 0)) ||
                        0;

                      const checked = groupReminderOptions[option.key];

                      return (
                        <div
                          key={option.key}
                          className={`chk-item ${checked ? "checked" : ""}`}
                          onClick={() => toggleGroupReminderOption(option.key)}
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          <div className={`chk-box ${checked ? "on" : ""}`}>
                            {checked ? "✓" : ""}
                          </div>

                          <div className="chk-content">
                            <div className="chk-title">
                              <span>
                                {option.icon} {option.title}
                              </span>

                              {/* <span className="count-badge count-gray">
                                {count}
                              </span> */}
                            </div>

                            <div className="chk-desc">{option.description}</div>
                          </div>
                        </div>
                      );
                    })}

                    <div className="section-divider"></div>

                    {/* CUSTOM MESSAGE */}

                    <div className="msg-lbl">
                      Custom Message
                      <span
                        style={{
                          fontWeight: 400,
                          textTransform: "none",
                          letterSpacing: 0,
                          color: "#d0d5dd",
                        }}
                      >
                        {" "}
                        (Optional)
                      </span>
                    </div>

                    <div className="msg-hint">
                      This message will be appended to the standard reminder
                      email.
                    </div>

                    <textarea
                      className="msg-ta"
                      value={groupReminderMessage}
                      maxLength={500}
                      placeholder="e.g. Please ensure your appraisal is completed before the deadline."
                      onChange={(e) => setGroupReminderMessage(e.target.value)}
                    />

                    <div className="char-count">
                      {groupReminderMessage.length} / 500
                    </div>

                    {/* PREVIEW */}

                    <div className="preview-strip">
                      <span className="preview-icon">👥</span>

                      <span className="preview-text">
                        Selected reminder types:
                      </span>

                      <span className="preview-count">
                        {
                          Object.values(groupReminderOptions).filter(Boolean)
                            .length
                        }
                      </span>
                    </div>
                  </div>

                  <div className="og-modal-footer">
                    <button
                      type="button"
                      className="btn-og btn-og-secondary"
                      disabled={sendingGroupReminder}
                      onClick={closeGroupReminderModal}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="btn-og btn-og-primary"
                      disabled={sendingGroupReminder}
                      onClick={handleSendGroupReminder}
                    >
                      {sendingGroupReminder ? (
                        <>
                          <i className="fa fa-spinner fa-spin"></i> Sending...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-paper-plane"></i> Send Reminders
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* ============================================================
    INDIVIDUAL NOTIFICATION CONFIRMATION
============================================================ */}

            {showNotificationModal && selectedEmployee && (
              <div className={`og-modal-overlay ${(showNotificationModal && selectedEmployee) ? 'open' : ''}`}>
                <div className="og-modal">
                  <div className="og-modal-header">
                    <div className="og-modal-title">
                      <i className="fa fa-bell acd-c-orange"></i> Send
                      Notification
                    </div>

                    <button
                      type="button"
                      className="og-modal-close"
                      onClick={closeNotificationModal}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </div>

                  <div className="modal-body">
                    {/* BREADCRUMB */}
                    <div className="modal-nav">
                      <span
                        className="nav-step"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setShowNotificationModal(false);
                          setShowReminderModal(true);
                        }}
                      >
                        All Options
                      </span>

                      <span className="nav-sep">›</span>

                      <span className="nav-current">
                        {
                          reminderOptions.find(
                            (item) => item.type === selectedNotificationType,
                          )?.icon
                        }{" "}
                        {
                          reminderOptions.find(
                            (item) => item.type === selectedNotificationType,
                          )?.title
                        }
                      </span>
                    </div>

                    {/* TYPE BANNER */}
                    <div className="type-banner">
                      <span className="type-icon">
                        {
                          reminderOptions.find(
                            (item) => item.type === selectedNotificationType,
                          )?.icon
                        }
                      </span>

                      <div>
                        <div className="type-label">
                          {
                            reminderOptions.find(
                              (item) => item.type === selectedNotificationType,
                            )?.title
                          }
                        </div>

                        <div className="type-sub">
                          Notification will be sent to{" "}
                          {getNotificationRecipient(
                            selectedNotificationType,
                            selectedEmployee,
                          )}
                        </div>
                      </div>
                    </div>

                    {/* RECIPIENT */}
                    <div className="section-lbl">Recipient</div>

                    <div className="recipient-card">
                      <div className="avatar confirm-recipient-avatar">
                        {getEmployeeName(selectedEmployee)
                          ?.split(" ")
                          ?.map((item) => item?.[0])
                          ?.join("")
                          ?.substring(0, 2)
                          ?.toUpperCase()}
                      </div>

                      <div>
                        <div className="rec-name">
                          {getNotificationRecipient(
                            selectedNotificationType,
                            selectedEmployee,
                          )}
                        </div>

                        <div className="rec-meta">
                          {getEmployeeName(selectedEmployee)}
                          {" · "}
                          {getValue(selectedEmployee, ["Email", "EmailId"], "")}
                        </div>

                        <span className="rec-tag">
                          {selectedNotificationType}
                        </span>
                      </div>
                    </div>

                    {/* CUSTOM MESSAGE */}
                    <div className="msg-lbl">
                      Custom Message
                      <span className="optional-label"> (Optional)</span>
                    </div>

                    <div className="msg-hint">
                      A standard reminder email will be sent. Add a personal
                      message if required.
                    </div>

                    <textarea
                      className="msg-ta"
                      value={notificationMessage}
                      maxLength={500}
                      placeholder="Add a personal message..."
                      onChange={(e) => setNotificationMessage(e.target.value)}
                    />

                    <div className="char-count">
                      {notificationMessage.length} / 500
                    </div>
                  </div>

                  <div className="og-modal-footer">
                    <button
                      type="button"
                      className="btn-og btn-og-secondary"
                      disabled={sendingNotification}
                      onClick={() => {
                        setShowNotificationModal(false);
                        setShowReminderModal(true);
                      }}
                    >
                      ‹ Back
                    </button>

                    <button
                      type="button"
                      className="btn-og btn-og-primary"
                      disabled={sendingNotification}
                      onClick={handleSendNotification}
                    >
                      {sendingNotification ? (
                        <>
                          <i className="fa fa-spinner fa-spin"></i> Sending...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-paper-plane"></i> Send Reminder
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================
    REMINDER SUCCESS
============================================================ */}

            {showReminderSuccess && (
              <div className={`og-modal-overlay ${showReminderSuccess ? 'open' : ''}`}>
                <div className="">
                  <div className="success-modal">
                    <div className="success-icon">✅</div>

                    <div className="success-title">Reminder Sent!</div>

                    <div className="success-sub">{reminderSuccessMessage}</div>

                    <button
                      type="button"
                      className="btn-done"
                      onClick={() => {
                        setShowReminderSuccess(false);
                        setReminderSuccessMessage("");
                      }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
             )} 
          </div>
        </div>
      </div>
      {loading ? (
        <Suspense>
          <ComponentLoader />
        </Suspense>
      ) : (
        ""
      )}
    </div>
  );
};

export default AssessmentCycleDetails;
